/*
 * pdp-product.js — behaviour of the extended product section. Vanilla JS, no dependencies.
 *
 *   <pdp-gallery>   scroll-snap gallery: thumbnails, prev/next, active state, show media by id
 *   <pdp-product>   variant selection → instantly updates price, compare-at price, discount badge,
 *                   "You save", stock / sold-out text, free-shipping progress, add-to-cart buttons
 *                   (main + sticky bar), gallery image and the URL (?variant=) — no page reload.
 *                   Also: sale countdown, sticky mobile add-to-cart bar, optional AJAX add to cart.
 *
 * Variant data comes from <script type="application/json" data-pdp-variants> (pdp-variants-json.liquid).
 * Texts come from data-* attributes that Liquid prints from the section / block schema settings.
 *
 * Events (bubbling, on the <pdp-product> element):
 *   pdp:variant-change   detail: { variant }
 *   pdp:cart-added       detail: { item }       (also dispatched on document, together with "cart:refresh")
 */
(() => {
  if (window.__pdpProduct) return;
  window.__pdpProduct = true;

  const $ = (root, selector) => root.querySelector(selector);
  const $$ = (root, selector) => Array.from(root.querySelectorAll(selector));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fill = (template, values) =>
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */
  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = $(this, '[data-pdp-track]');
      if (!this.track) return;
      this.slides = $$(this, '[data-pdp-slide]');
      this.thumbs = $$(this, '[data-pdp-thumb]');
      this.thumbStrip = $(this, '[data-pdp-thumbs]');
      this.prev = $(this, '[data-pdp-prev]');
      this.next = $(this, '[data-pdp-next]');
      this.current = 0;

      this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo(index)));
      if (this.prev) this.prev.addEventListener('click', () => this.goTo(this.current - 1));
      if (this.next) this.next.addEventListener('click', () => this.goTo(this.current + 1));

      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      this.setActive(0);
      const initial = this.dataset.initialMedia;
      if (initial) this.showMedia(initial, true);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    goTo(index, instant) {
      if (!this.slides || !this.slides.length) return;
      const clamped = Math.max(0, Math.min(this.slides.length - 1, index));
      const slide = this.slides[clamped];
      this.track.scrollTo({ left: slide.offsetLeft, behavior: instant || reduceMotion() ? 'auto' : 'smooth' });
      this.setActive(clamped);
    }

    showMedia(mediaId, instant) {
      if (!this.slides) return;
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) this.goTo(index, instant);
    }

    setActive(index) {
      if (index < 0) return;
      this.current = index;
      this.thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;

      // Keep the active thumbnail visible by scrolling the strip itself (never the page).
      const thumb = this.thumbs[index];
      const strip = this.thumbStrip;
      if (thumb && strip && strip.scrollWidth > strip.clientWidth) {
        const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
        strip.scrollTo({ left, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Product (variants, price, stock, sticky bar, add to cart)           */
  /* ------------------------------------------------------------------ */
  class PdpProduct extends HTMLElement {
    connectedCallback() {
      const data = $(this, '[data-pdp-variants]');
      try {
        this.variants = data ? JSON.parse(data.textContent) : [];
      } catch (error) {
        this.variants = [];
      }

      this.form = $(this, 'form[data-pdp-form]');
      this.idInput = this.form && $(this.form, 'input[name="id"]');
      this.qtyInput = this.form && $(this.form, 'input[name="quantity"]');
      this.groups = $$(this, '[data-pdp-option-group]');
      this.labels = {
        add: this.dataset.labelAdd || 'Add to cart',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        unavailable: this.dataset.labelUnavailable || 'Unavailable',
      };

      const initialId = this.idInput ? Number(this.idInput.value) : null;
      this.variant = this.variants.find((v) => v.id === initialId) || this.variants[0] || null;

      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      window.addEventListener('popstate', this.onPopState);

      this.render();
      this.initCountdown();
      this.initSticky();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('input', this.onInput);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      window.removeEventListener('popstate', this.onPopState);
      if (this.timer) clearInterval(this.timer);
      if (this.stickyObserver) this.stickyObserver.disconnect();
      document.documentElement.style.removeProperty('--pdp-sticky-h');
    }

    /* ---- events ---- */

    onChange(event) {
      if (event.target.closest('[data-pdp-option-group]')) this.selectFromOptions(true);
    }

    onInput(event) {
      if (event.target === this.qtyInput) this.updateFreeShipping();
    }

    onClick(event) {
      const minus = event.target.closest('[data-pdp-qty-minus]');
      const plus = event.target.closest('[data-pdp-qty-plus]');
      if ((minus || plus) && this.qtyInput) {
        const min = Number(this.qtyInput.min) || 1;
        const next = (Number(this.qtyInput.value) || min) + (plus ? 1 : -1);
        this.qtyInput.value = Math.max(min, next);
        this.updateFreeShipping();
      }
    }

    onPopState() {
      const id = Number(new URL(window.location.href).searchParams.get('variant'));
      const variant = this.variants.find((v) => v.id === id);
      if (variant && variant !== this.variant) {
        this.setOptionsFromVariant(variant);
        this.selectFromOptions(false);
      }
    }

    /* ---- variant selection ---- */

    selectedOptions() {
      return this.groups.map((group) => {
        const select = $(group, 'select');
        if (select) return select.value;
        const checked = $(group, 'input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    setOptionsFromVariant(variant) {
      this.groups.forEach((group, index) => {
        const value = variant.options[index];
        const select = $(group, 'select');
        if (select) select.value = value;
        $$(group, 'input[type="radio"]').forEach((radio) => (radio.checked = radio.value === value));
      });
    }

    findVariant(options) {
      return this.variants.find((v) => v.options.every((option, i) => option === options[i])) || null;
    }

    selectFromOptions(updateUrl) {
      const variant = this.findVariant(this.selectedOptions());
      this.variant = variant;
      if (variant && this.idInput) this.idInput.value = variant.id;

      this.render();

      if (variant) {
        if (updateUrl && window.history && history.replaceState && this.dataset.updateUrl !== 'false') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          history.replaceState({}, '', url);
        }
        const gallery = $(this, 'pdp-gallery');
        if (gallery && variant.mediaId && gallery.showMedia) gallery.showMedia(variant.mediaId);
        this.dispatchEvent(new CustomEvent('pdp:variant-change', { bubbles: true, detail: { variant } }));
      }
    }

    /* ---- rendering ---- */

    render() {
      this.updatePrice();
      this.updateStock();
      this.updateButtons();
      this.updateFreeShipping();
      this.markAvailability();
    }

    updatePrice() {
      const v = this.variant;
      if (!v) return;
      $$(this, '[data-pdp-price]').forEach((el) => {
        el.textContent = v.priceF;
        el.classList.toggle('pdp-price__current--sale', v.compare > v.price);
      });

      const onSale = v.compare > v.price;
      $$(this, '[data-pdp-compare]').forEach((el) => {
        el.textContent = onSale ? v.compareF : '';
        el.hidden = !onSale;
      });
      $$(this, '[data-pdp-badge]').forEach((el) => {
        const show = onSale && v.pct > 0;
        el.textContent = show ? `-${v.pct}%` : '';
        el.hidden = !show;
      });
      $$(this, '[data-pdp-save]').forEach((el) => {
        const block = el.closest('[data-pdp-price-block]');
        el.textContent = onSale ? fill(block && block.dataset.saveText, { amount: v.saveF }) : '';
        el.hidden = !onSale;
      });
    }

    updateStock() {
      const el = $(this, '[data-pdp-stock]');
      if (!el) return;
      const v = this.variant;
      const low = Number(el.dataset.lowThreshold) || 0;
      let state = '';
      let text = '';

      if (!v) {
        state = 'out';
        text = this.labels.unavailable;
      } else if (!v.available) {
        state = 'out';
        text = this.labels.soldOut;
      } else if (low > 0 && v.stock > 0 && v.stock <= low) {
        state = 'low';
        text = fill(el.dataset.lowText, { count: v.stock });
      }

      el.dataset.state = state;
      el.hidden = !state;
      const label = $(el, '[data-pdp-stock-text]');
      if (label) label.textContent = text;
    }

    updateButtons() {
      const v = this.variant;
      const available = !!(v && v.available);
      const text = !v ? this.labels.unavailable : v.available ? this.labels.add : this.labels.soldOut;
      $$(this, '[data-pdp-atc]').forEach((button) => {
        button.disabled = !available;
        const label = $(button, '[data-pdp-atc-label]');
        if (label) label.textContent = text;
      });
    }

    quantity() {
      return Math.max(1, Number(this.qtyInput && this.qtyInput.value) || 1);
    }

    formatMoney(cents, el) {
      try {
        return new Intl.NumberFormat(el.dataset.locale || undefined, {
          style: 'currency',
          currency: el.dataset.currency || 'USD',
        }).format(cents / 100);
      } catch (error) {
        return (cents / 100).toFixed(2);
      }
    }

    updateFreeShipping() {
      const el = $(this, '[data-pdp-freeship]');
      if (!el) return;
      if (this.cartTotal === undefined) this.cartTotal = Number(el.dataset.cartTotal) || 0;
      const threshold = Number(el.dataset.threshold) || 0;
      if (!threshold) return;

      const price = this.variant ? this.variant.price : 0;
      const total = this.cartTotal + price * this.quantity();
      const percent = Math.min(100, Math.floor((total / threshold) * 100)); // 100 only once it is really reached

      let text;
      if (total >= threshold) {
        text = el.dataset.textReached;
      } else if (this.cartTotal > 0) {
        text = fill(el.dataset.textProgress, { remaining: this.formatMoney(threshold - total, el) });
      } else {
        text = fill(el.dataset.textIntro, { amount: this.formatMoney(threshold, el) });
      }

      const textEl = $(el, '[data-pdp-freeship-text]');
      const labelEl = $(el, '[data-pdp-freeship-label]');
      const barEl = $(el, '[data-pdp-freeship-bar]');
      const progress = $(el, '[data-pdp-freeship-progress]');
      if (textEl) textEl.textContent = text;
      if (labelEl) labelEl.textContent = text;
      if (barEl) barEl.style.width = `${percent}%`;
      if (progress) progress.setAttribute('aria-valuenow', String(percent));
      el.classList.toggle('is-reached', total >= threshold);
    }

    /* Mark option values that are sold out / not available for the other current selections. */
    markAvailability() {
      const selected = this.selectedOptions();
      this.groups.forEach((group, groupIndex) => {
        $$(group, '[data-pdp-option]').forEach((input) => {
          const candidate = selected.slice();
          candidate[groupIndex] = input.value;
          const match = this.findVariant(candidate);
          const state = !match ? 'unavailable' : match.available ? 'available' : 'soldout';

          if (input.tagName === 'OPTION') {
            if (!input.dataset.baseText) input.dataset.baseText = input.textContent;
            const suffix = state === 'soldout' ? this.labels.soldOut : state === 'unavailable' ? this.labels.unavailable : '';
            input.textContent = suffix ? `${input.dataset.baseText} — ${suffix}` : input.dataset.baseText;
          } else {
            const label = input.id && $(group, `label[for="${CSS.escape(input.id)}"]`);
            if (label) label.dataset.state = state;
          }
        });
      });
    }

    /* ---- countdown ---- */

    initCountdown() {
      const el = $(this, '[data-pdp-countdown]');
      if (!el) return;
      const end = Number(el.dataset.end) * 1000;
      const parts = {
        d: $(el, '[data-cd="d"]'),
        h: $(el, '[data-cd="h"]'),
        m: $(el, '[data-cd="m"]'),
        s: $(el, '[data-cd="s"]'),
      };
      const pad = (n) => String(n).padStart(2, '0');

      const tick = () => {
        const remaining = end - Date.now();
        if (!(remaining > 0)) {
          el.hidden = true; // the sale ended: only a real, future date is ever shown
          if (this.timer) clearInterval(this.timer);
          return;
        }
        const seconds = Math.floor(remaining / 1000);
        parts.d.textContent = Math.floor(seconds / 86400);
        parts.h.textContent = pad(Math.floor((seconds % 86400) / 3600));
        parts.m.textContent = pad(Math.floor((seconds % 3600) / 60));
        parts.s.textContent = pad(seconds % 60);
      };

      tick();
      this.timer = setInterval(tick, 1000);
    }

    /* ---- sticky mobile add-to-cart bar ---- */

    initSticky() {
      this.sticky = $(this, '[data-pdp-sticky]');
      const anchor = $(this, '[data-pdp-buy-anchor]');
      if (!this.sticky || !anchor || !('IntersectionObserver' in window)) return;

      this.setSticky(false);
      this.stickyObserver = new IntersectionObserver(([entry]) => {
        // Show only once the real buy buttons have scrolled out of view *upwards*.
        this.setSticky(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.stickyObserver.observe(anchor);
    }

    setSticky(visible) {
      this.sticky.dataset.visible = String(visible);
      this.sticky.toggleAttribute('inert', !visible);
      document.documentElement.style.setProperty('--pdp-sticky-h', visible ? `${this.sticky.offsetHeight}px` : '0px');
    }

    /* ---- add to cart ---- */

    message(text, type, withCartLink) {
      const box = $(this, '[data-pdp-message]');
      if (!box) return;
      box.textContent = text;
      box.dataset.type = type || '';
      if (withCartLink && this.dataset.cartUrl) {
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = this.dataset.labelViewCart || 'View cart';
        box.append(' ', link);
      }
    }

    async onSubmit(event) {
      if (this.dataset.addBehavior !== 'ajax') return; // default: normal form post → cart page
      event.preventDefault();
      if (this.adding || !this.variant || !this.variant.available) return;

      this.adding = true;
      const buttons = $$(this, '[data-pdp-atc]');
      buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
      this.message('', '');

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form),
        });
        const item = await response.json();
        if (!response.ok) throw new Error(item.description || item.message || 'Could not add to cart');

        if (this.cartTotal === undefined) {
          const fs = $(this, '[data-pdp-freeship]');
          this.cartTotal = fs ? Number(fs.dataset.cartTotal) || 0 : 0;
        }
        this.cartTotal += this.variant.price * this.quantity();
        this.updateFreeShipping();
        this.message(this.dataset.labelAdded || 'Added to cart', 'success', true);

        this.dispatchEvent(new CustomEvent('pdp:cart-added', { bubbles: true, detail: { item } }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { item } }));
      } catch (error) {
        this.message(error.message, 'error');
      } finally {
        this.adding = false;
        buttons.forEach((button) => button.removeAttribute('aria-busy'));
      }
    }
  }

  if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);
  if (!customElements.get('pdp-product')) customElements.define('pdp-product', PdpProduct);
})();
;(function(){if(window.__pdpZoomInit)return;window.__pdpZoomInit=true;var st=document.createElement('style');st.textContent='.pdp-gallery__slide{position:relative}.pdp-gallery__slide img{cursor:zoom-in}.pdp-zoom-btn{position:absolute;top:12px;right:12px;width:40px;height:40px;padding:0;border:0;border-radius:50%;background:#fff;color:#111;box-shadow:0 1px 4px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;cursor:pointer;z-index:2}.pdp-zoom-btn svg{width:20px;height:20px}.pdp-zoom-overlay{position:fixed;top:0;right:0;bottom:0;left:0;z-index:100000;background:rgba(0,0,0,.92);display:flex;align-items:center;justify-content:center;overflow:hidden}.pdp-zoom-overlay img{max-width:92vw;max-height:92vh;object-fit:contain;cursor:zoom-in;transition:transform .2s ease;user-select:none;-webkit-user-drag:none}.pdp-zoom-overlay.is-zoomed img{cursor:zoom-out;transform:scale(2.4)}.pdp-zoom-close{position:absolute;top:16px;right:16px;width:44px;height:44px;padding:0;border:0;border-radius:50%;background:#fff;color:#111;font-size:28px;line-height:1;cursor:pointer;z-index:2}';document.head.appendChild(st);var ICON='<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><path d="M20 20l-3.5-3.5M11 8v6M8 11h6"></path></svg>';function bigSrc(s){try{var u=new URL(s,location.href);u.searchParams.set('width','2000');return u.toString()}catch(e){return s}}function pan(im,x,y){var px=Math.max(0,Math.min(1,(x-im.offsetLeft)/im.offsetWidth))*100;var py=Math.max(0,Math.min(1,(y-im.offsetTop)/im.offsetHeight))*100;im.style.transformOrigin=px+'% '+py+'%'}function openZoom(src){var ov=document.createElement('div');ov.className='pdp-zoom-overlay';ov.setAttribute('role','dialog');ov.setAttribute('aria-modal','true');ov.setAttribute('aria-label','Zoomed product image');var im=document.createElement('img');im.alt=src.alt||'';im.src=bigSrc(src.currentSrc||src.src);var cb=document.createElement('button');cb.type='button';cb.className='pdp-zoom-close';cb.setAttribute('aria-label','Close');cb.innerHTML='&times;';ov.appendChild(im);ov.appendChild(cb);document.body.appendChild(ov);var po=document.body.style.overflow;document.body.style.overflow='hidden';function onKey(e){if(e.key==='Escape')close()}function close(){ov.remove();document.body.style.overflow=po;document.removeEventListener('keydown',onKey)}document.addEventListener('keydown',onKey);cb.addEventListener('click',function(e){e.stopPropagation();close()});ov.addEventListener('click',function(e){if(e.target===ov)close()});im.addEventListener('click',function(e){e.stopPropagation();pan(im,e.clientX,e.clientY);ov.classList.toggle('is-zoomed')});ov.addEventListener('mousemove',function(e){if(ov.classList.contains('is-zoomed'))pan(im,e.clientX,e.clientY)});ov.addEventListener('touchmove',function(e){if(ov.classList.contains('is-zoomed')&&e.touches[0])pan(im,e.touches[0].clientX,e.touches[0].clientY)},{passive:true});cb.focus()}document.addEventListener('click',function(e){var sl=e.target.closest&&e.target.closest('.pdp-gallery__slide');if(!sl||e.target.closest('a'))return;var im=sl.querySelector('img');if(!im)return;e.preventDefault();openZoom(im)});function addButtons(){document.querySelectorAll('.pdp-gallery__slide:not([data-zoom-ready])').forEach(function(sl){sl.setAttribute('data-zoom-ready','');if(!sl.querySelector('img'))return;var b=document.createElement('button');b.type='button';b.className='pdp-zoom-btn';b.setAttribute('aria-label','Zoom image');b.innerHTML=ICON;sl.appendChild(b)})}function init(){addButtons();var g=document.querySelector('pdp-gallery');if(g&&window.MutationObserver)new MutationObserver(addButtons).observe(g,{childList:true,subtree:true})}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init()})();